import { useId, useState } from "react";
import { timeTrackCategories, timeTrackCategoryLabels } from "@shared/timetrack";
import type { TimeTrackEntry, TimeTrackTarget } from "@shared/timetrack";
import { ProjectPicker } from "./project-picker";
import { useSelectAllOnFocus } from "./select-on-focus";
import { minuteDuration, parseDuration } from "./time-utils";

export function TargetField({ targets, value, onChange, disabled = false }: {
  targets: TimeTrackTarget[]; value: string; onChange: (value: string) => void; disabled?: boolean;
}) {
  return <ProjectPicker targets={targets} value={value} onChange={onChange} disabled={disabled}
    label="Work item" placeholder="Choose a project or work item" includeWorkItems required />;
}

export function CategoryField({ value, onChange, disabled = false }: {
  value: TimeTrackEntry["category"]; onChange: (value: TimeTrackEntry["category"]) => void; disabled?: boolean;
}) {
  return <label className="tt-field"><span>Kind of work</span><select value={value} disabled={disabled} onChange={e => onChange(e.target.value as TimeTrackEntry["category"])}>
    {timeTrackCategories.map(category => <option value={category} key={category}>{timeTrackCategoryLabels[category]}</option>)}
  </select></label>;
}

export interface DurationParts { text: string; originalSeconds: number }
export function durationParts(seconds: number): DurationParts {
  return { text: minuteDuration(seconds), originalSeconds: seconds };
}
export function durationValue(parts: DurationParts) {
  const parsed = parseDuration(parts.text);
  if (parsed.error) return NaN;
  // Merely focusing, normalizing or retyping the displayed minutes must not round saved time.
  return parsed.formatted === minuteDuration(parts.originalSeconds) ? parts.originalSeconds : parsed.seconds!;
}
export function DurationFields({ value, onChange, disabled = false, label = "Duration" }: {
  value: DurationParts; onChange: (parts: DurationParts) => void; disabled?: boolean; label?: string;
}) {
  const id = useId();
  const [touched, setTouched] = useState(false);
  const result = parseDuration(value.text);
  const error = touched ? result.error : undefined;
  const select = useSelectAllOnFocus();
  return <div className="tt-duration">
    <label className="tt-field" htmlFor={id}><span>{label} (H:MM)</span>
      <input id={id} aria-label={label} type="text" inputMode="text" required disabled={disabled} autoComplete="off"
        aria-invalid={!!error} aria-describedby={`${id}-help${error ? ` ${id}-error` : ""}`}
        value={value.text} placeholder="1:30"
        onChange={e => { setTouched(true); onChange({ ...value, text: e.target.value }); }}
        onFocus={select.onFocus} onMouseUp={select.onMouseUp}
        onBlur={() => { select.onBlur(); setTouched(true); if (!result.error) onChange({ ...value, text: result.formatted! }); }} />
    </label>
    <small id={`${id}-help`}>Hours and minutes. 130 → 1:30; 45 → 0:45. Unchanged durations keep their exact recorded time.</small>
    {error && <p className="tt-error" id={`${id}-error`} role="alert">{error}</p>}
  </div>;
}
